import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import type { EntityPeekProps } from "@/components/hub/entityPeekTypes";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn, EM_DASH } from "@/lib/utils";

import type { InventoryDiscrepancySkuContextRow } from "./inventoryDiscrepancyApi";
import { formatSignedSkuContextQuantity } from "./inventoryDiscrepancySkuContext";
import {
  resolveInventoryDiscrepancyContextReference,
  type InventoryDiscrepancyContextReferenceMegaventoryLink,
  type InventoryDiscrepancyContextReferenceResolution,
} from "./inventoryDiscrepancyContextReferenceLinkModel";

export { resolveInventoryDiscrepancyContextReference } from "./inventoryDiscrepancyContextReferenceLinkModel";
export type {
  InventoryDiscrepancyContextReferenceMegaventoryLink,
  InventoryDiscrepancyContextReferenceResolution,
} from "./inventoryDiscrepancyContextReferenceLinkModel";

const HUB_LINK_CLASS =
  "font-mono font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus:ring-offset-1";

const MEGAVENTORY_LINK_CLASS =
  "inline-flex shrink-0 text-slate-400 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus:ring-offset-1";

function contextPeekRows(
  row: InventoryDiscrepancySkuContextRow,
  resolution: InventoryDiscrepancyContextReferenceResolution,
): ReactNode {
  const extraRows: Array<{ label: string; value: ReactNode }> = [];
  switch (resolution.hoverHintKey) {
    case "purchase_invoice_po":
    case "purchase_invoice_hub":
      extraRows.push(
        { label: "Purchase invoice", value: row.entity_id != null ? `#${row.entity_id}` : EM_DASH },
        { label: "Purchase order", value: row.entity_ref?.trim() || EM_DASH },
      );
      break;
    case "purchase_invoice_mv":
      extraRows.push({ label: "Megaventory", value: row.mv_entity_id ?? EM_DASH });
      break;
    case "legacy_transfer":
      extraRows.push(
        { label: "Document", value: "Legacy Megaventory goods transfer" },
        { label: "MV document", value: row.mv_entity_id ?? EM_DASH },
      );
      break;
    case "hub_transfer":
      extraRows.push({
        label: "Transfer order",
        value: row.entity_id != null ? `#${row.entity_id}` : EM_DASH,
      });
      break;
    case "inbound_receipt_source":
      extraRows.push(
        { label: "Receipt", value: row.reference ?? EM_DASH },
        {
          label: "Source",
          value: `${row.entity_id ?? ""} ${row.entity_ref ?? ""}`.trim() || EM_DASH,
        },
        {
          label: "Arrival container",
          value: row.arrival_package_no?.trim() || EM_DASH,
        },
      );
      break;
    case "inbound_receipt_queue":
      extraRows.push({ label: "Receipt", value: row.reference ?? EM_DASH });
      break;
    case "adjustment":
      extraRows.push({ label: "Megaventory", value: row.mv_entity_id ?? EM_DASH });
      break;
    default:
      break;
  }

  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Kind", value: row.kind },
        { label: "Reference", value: row.reference?.trim() || EM_DASH },
        ...extraRows,
        { label: "Quantity", value: formatSignedSkuContextQuantity(row) },
        { label: "Status / detail", value: row.detail?.trim() || EM_DASH },
        { label: "Date", value: row.occurred_at?.slice(0, 10) ?? EM_DASH },
      ]}
    />
  );
}

function ReferenceLinkWithPeek({
  trigger,
  hoverHint,
}: {
  trigger: ReactNode;
  hoverHint: ReactNode;
}): JSX.Element {
  return (
    <HoverHintTrigger align="start" trigger={trigger}>
      {hoverHint}
    </HoverHintTrigger>
  );
}

function MegaventoryExternalLink({
  link,
}: {
  link: InventoryDiscrepancyContextReferenceMegaventoryLink;
}): JSX.Element {
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      className={MEGAVENTORY_LINK_CLASS}
      aria-label={link.ariaLabel}
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

function isWorkOrderContextKind(kind: string): boolean {
  return kind === "work_order" || kind === "work_order_allocation";
}

function WorkOrderContextReferenceTargets({
  resolved,
  className,
  buildOrderId,
}: {
  resolved: InventoryDiscrepancyContextReferenceResolution;
  className?: string;
  buildOrderId: number;
}): JSX.Element {
  const peek: EntityPeekProps = { kind: "work_order", buildOrderId };
  const label = resolved.label;
  return (
    <span className={cn("inline-flex items-center gap-1 font-mono", className)}>
      <WorkOrderDetailLink buildOrderId={buildOrderId} workOrderNumber={label} className="font-mono" />
      {resolved.megaventory ? (
        <DetailLinkWithEntityPeek
          peek={peek}
          trigger={<MegaventoryExternalLink link={resolved.megaventory} />}
        />
      ) : null}
    </span>
  );
}

function ContextReferenceTargets({
  resolved,
  className,
}: {
  resolved: InventoryDiscrepancyContextReferenceResolution;
  className?: string;
}): JSX.Element {
  const label = resolved.label;

  return (
    <span className={cn("inline-flex items-center gap-1 font-mono", className)}>
      {resolved.hub ? (
        <Link to={resolved.hub.to} className={HUB_LINK_CLASS}>
          {label}
        </Link>
      ) : (
        <span>{label}</span>
      )}
      {resolved.megaventory ? <MegaventoryExternalLink link={resolved.megaventory} /> : null}
    </span>
  );
}

export function InventoryDiscrepancyContextReferenceLink({
  row,
  className,
}: {
  row: InventoryDiscrepancySkuContextRow;
  className?: string;
}): JSX.Element {
  const resolved = resolveInventoryDiscrepancyContextReference(row);
  const label = row.reference?.trim() || EM_DASH;

  if (resolved == null) {
    return <span className={cn("font-mono", className)}>{label}</span>;
  }

  const buildOrderId = resolved.hub?.prefetchBuildOrderId;
  if (buildOrderId != null && isWorkOrderContextKind(row.kind)) {
    return (
      <WorkOrderContextReferenceTargets
        resolved={resolved}
        className={className}
        buildOrderId={buildOrderId}
      />
    );
  }

  const hoverHint = contextPeekRows(row, resolved);

  return (
    <ReferenceLinkWithPeek
      hoverHint={hoverHint}
      trigger={<ContextReferenceTargets resolved={resolved} className={className} />}
    />
  );
}
